import type { ContentClass, TitleFormat } from '@Types/Shared/Common';
import type { BlockKey } from '@Types/Shared/Options';

import { cn } from '@/src/Blocks/_components/ui/utils';
import { type TxtImgFrontType, type TxtImgRepeaterType } from '@/src/Types/Blocks/TxtImg';
import { type HeadingType } from '@/src/Types/Shared/Heading';
import Heading from '@/src/Views/Shared/Heading/Heading';
import Html from '@Views/Shared/Html';

export default function TxtImgFullWidth(props: TxtImgFrontType) {
	const {
		id,
		titleFormat,
		title,
		description,
		images,
		headingAlign,
		headingFontClassName,
		contentFontClass,
		context,
		sectionDecoration,
	} = props;

	const headingProps: HeadingType = {
		title: title,
		description: description,
		titleFormat: titleFormat,
		headingAlign: headingAlign,
		headingFontClassName: headingFontClassName,
		context: context,
	};

	const paddingHeading = cn(
		'relative z-[11] col-span-12 col-start-2',
		sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave'
			? ''
			: 'pt-3.5 tablet:pt-6 desktop:pt-12'
	);

	const wrapperPadding = cn(
		'minos-grid bg-minos-txtImg-bg-color',
		sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave'
			? 'pt-8 pb-16 tablet:pt-12 tablet:pb-24 desktop:pb-32'
			: ''
	);

	return (
		<div id={id} className={wrapperPadding}>
			{/* mobile breakpoints handle in component */}
			<Heading data-aos='fade-up' className={paddingHeading} {...headingProps} />

			<ul data-aos='fade-up' className='col-span-full grid-cols-4 gap-8 sm:grid-cols-12'>
				{images?.map((image: TxtImgRepeaterType) => {
					return (
						<TxtImgRepeater
							key={image.id}
							repeater={image}
							contentFontClass={contentFontClass}
							context={context}
						/>
					);
				})}
			</ul>
		</div>
	);
}

const TxtImgRepeater = ({
	repeater,
	contentFontClass,
	context,
}: {
	repeater: TxtImgRepeaterType;
	contentFontClass?: ContentClass;
	context?: BlockKey;
}) => {
	const { id, title, description, titleFormat, imgPosition } = repeater;

	const views = {
		left: (
			<ImgLeft
				id={id}
				titleFormat={titleFormat}
				description={description}
				title={title}
				image={repeater.imgUrl}
				imageAlt={repeater.imageAlt}
				contentFontClass={contentFontClass}
				context={context}
			/>
		),
		right: (
			<ImgRight
				id={id}
				titleFormat={titleFormat}
				description={description}
				title={title}
				image={repeater.imgUrl}
				imageAlt={repeater.imageAlt}
				contentFontClass={contentFontClass}
				context={context}
			/>
		),
	};

	return <>{views[imgPosition]}</>;
};

type ImgPositionType = {
	id: string;
	titleFormat: TitleFormat;
	description: any;
	image: string | undefined;
	title: string;
	imageAlt: string | undefined;
	contentFontClass?: ContentClass;
	context?: BlockKey;
};

const ImgLeft = (props: ImgPositionType) => {
	const { id, titleFormat, description, title, image, contentFontClass, context } = props;
	return (
		<li
			className='flex flex-col bg-minos-txtImg-bg-color tablet:flex-row'
			data-img='img-left'
			id={id}
		>
			<>
				{image && (
					<div className='relative z-[11] w-full tablet:w-1/2 '>
						<img className='aspect-[4/3] w-full object-cover tablet:aspect-square' src={image} alt='' />
					</div>
				)}
			</>
			<div className='relative z-[11] w-full p-4 tablet:my-auto tablet:block tablet:w-1/2 tablet:px-16 desktop:px-32'>
				{titleFormat === 'h4' ? (
					<h4 className={`text-xl ${contentFontClass?.title}`}>{title}</h4>
				) : (
					<h3 className={`text-2xl  ${contentFontClass?.title}`}>{title}</h3>
				)}
				<div data-content='minos-content'>
					<Html context={context} className={contentFontClass?.text} html={description} />
				</div>
			</div>
		</li>
	);
};

const ImgRight = (props: ImgPositionType) => {
	const { id, titleFormat, description, title, image, contentFontClass, context } = props;
	return (
		<li
			className='flex flex-col bg-minos-txtImg-bg-color tablet:flex-row'
			data-img='img-right'
			id={id}
		>
			<div className='relative z-[11] w-full p-4 tablet:my-auto tablet:block tablet:w-1/2 tablet:px-16 desktop:px-32'>
				{titleFormat === 'h3' ? (
					<h3 className={`text-2xl ${contentFontClass?.title}`}>{title}</h3>
				) : (
					<h2 className={`text-3xl  ${contentFontClass?.title}`}>{title}</h2>
				)}
				<div data-content='minos-content'>
					<Html context={context} className={contentFontClass?.text} html={description} />
				</div>
			</div>
			<>
				{image && (
					<div className='relative z-[11] w-full tablet:w-1/2'>
						<img className='aspect-[4/3] w-full object-cover tablet:aspect-square' src={image} alt='' />
					</div>
				)}
			</>
		</li>
	);
};
